<template>
  <form @submit.prevent="onSubmit" novalidate>
    <div v-for="field in schema" :key="field.name" :style="field.customStyle" :class="formGroupClass">
      <SelectInput
        v-if="isSelectInput(field.type)"
        :name="field.name"
        :options="field.options"
        :disabled="field.disabled"
        :attrs="field"
        :initial-option="field.initialOption"
      />
      <CheckboxInput v-else-if="isCheckboxInput(field.type)" :label="field.label" :name="field.name" :attrs="field" />
      <PasswordInput
        v-else-if="isPasswordInput(field.type)"
        :label="field.label"
        :name="field.name"
        :attrs="field"
        :errors="errors"
      />
      <StandardInput
        v-else
        :label="field.label"
        :as="field.as"
        :name="field.name"
        :mask="field.mask"
        :cardBrand="field.cardBrand"
        :attrs="field"
        :errors="errors"
        :inputmode="inputmode"
      />
      <div class="grp-error">
        <img
          v-if="emailErrorMessage && field.name === 'email' && !errors.hasOwnProperty('email')"
          src="@/assets/img/icon-error.svg"
          alt=""
          class="grp-error--icon"
        />
        <p
          v-if="emailErrorMessage && field.name === 'email' && !errors.hasOwnProperty('email')"
          class="grp-error--message"
        >
          {{ emailErrorMessage }}
        </p>
      </div>
      <BaseErrorMessage :name="field.name" :has-icon="hasIconMessage" />
    </div>

    <slot name="middle-complement"></slot>

    <button
      v-show="showButton"
      type="submit"
      class="grp-button-main"
      :disabled="
        !meta.valid ||
        isSubmitting ||
        Object.values(submitExtraValidations).includes(true) ||
        (emailStatus === 'invalid' && !overrideAllowed)
      "
    >
      <BaseLoader :show="isSubmitting" :mode="loaderMode" />
      <slot name="button-submit" :isSubmitting="isSubmitting" :submitExtraValidations="submitExtraValidations" />
      {{ submitButtonLabel }}
    </button>

    <slot name="end-complement"></slot>
  </form>
</template>

<script setup>
import { computed, ref, watch, nextTick } from 'vue';
import { useForm } from 'vee-validate';
import BaseLoader from '@/components/BaseLoader';
import SelectInput from '@/components/form/SelectInput';
import StandardInput from '@/components/form/StandardInput';
import CheckboxInput from '@/components/form/CheckboxInput';
import PasswordInput from '@/components/form/PasswordInput';
import BaseErrorMessage from '@/components/BaseErrorMessage';
import debounce from 'lodash/debounce';
import lmsClient from '@/http/lms';

const emit = defineEmits(['changeInstallment', 'changeDocument']);
const props = defineProps({
  mode: { type: String, default: '' },
  schema: { type: Object, required: true },
  submit: { type: Function, required: true },
  showButton: { type: Boolean, default: true },
  hasIconMessage: { type: Boolean, default: false },
  initialErrors: { type: Object, default: () => {} },
  hasFloatingLabel: { type: Boolean, default: false },
  submitButtonLabel: { type: String, default: '' },
  submitExtraValidations: {
    type: Object,
    default: () => {
      return {};
    },
  },
  enableEmailValidation: { type: Boolean, default: false },
});

const emailStatus = ref(null); // statuss null, "valid", "invalid", "override"
const lastEmailChecked = ref('');
const overrideAllowed = ref(false);
const emailErrorMessage = ref('');

const formGroupClass = computed(() => (props.hasFloatingLabel ? 'grp-form-group--float' : 'grp-form-group'));
const loaderMode = computed(() => {
  if (props.mode) {
    return props.mode;
  }

  return props.hasFloatingLabel ? 'light' : 'dark';
});

const {
  handleSubmit,
  errors,
  isSubmitting,
  meta,
  values: formValues,
} = useForm({ initialErrors: props.initialErrors });

const validateEmailAPI = async (email) => {
  try {
    const response = await lmsClient.post(
      `https://n8n-prd.ogrupoprimo.com/webhook/email-validator?email=${encodeURIComponent(email)}`
    );
    return response.data.risk;
  } catch (error) {
    console.error('Erro ao validar e-mail:', error);
    return null;
  }
};

const onSubmit = handleSubmit(async (data) => {
  if (emailStatus.value === 'invalid' && formValues.email === lastEmailChecked.value) {
    emailStatus.value = 'override';
    overrideAllowed.value = true;
    emailErrorMessage.value = '';
  }
  await props.submit(data);
});

watch(
  () => formValues.email,
  debounce(async (newEmail) => {
    if (!newEmail) {
      emailStatus.value = null;
      emailErrorMessage.value = '';
      return;
    }

    if (!props.enableEmailValidation) {
      emailStatus.value = 'valid';
      emailErrorMessage.value = '';
      return;
    }

    if (overrideAllowed.value && newEmail === lastEmailChecked.value) {
      return;
    }

    const risk = await validateEmailAPI(newEmail);

    if (risk === 'high' || risk === 'very_high') {
      if (newEmail === lastEmailChecked.value) {
        emailStatus.value = 'override';
        overrideAllowed.value = true;
        emailErrorMessage.value = '';
      } else {
        emailStatus.value = 'invalid';
        overrideAllowed.value = false;
        lastEmailChecked.value = newEmail;
        emailErrorMessage.value = 'Este campo deve possuir um e-mail válido (ex: xxx@xxx.com)';
        await nextTick();
      }
    } else {
      emailStatus.value = 'valid';
      overrideAllowed.value = false;
      emailErrorMessage.value = '';
    }
  }, 700)
);

watch(
  () => formValues.installments,
  (value) => {
    emit('changeInstallment', value);
  }
);

watch(
  () => formValues.number,
  (value) => {
    emit('changeCCNumber', value);
  }
);

watch(
  () => formValues.cvv,
  (value) => {
    emit('changeCCV', value);
  }
);

watch(
  () => formValues.userDocument,
  debounce((newValue) => {
    emit('changeDocument', newValue);
  }, 700)
);

const isCheckboxInput = (type) => type === 'checkbox';
const isPasswordInput = (type) => type === 'password';
const isSelectInput = (type) => typeof type === 'string' && type === 'select';
</script>

<style lang="scss" scoped>
.grp-button-main {
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
}

.grp-error {
  display: inline-flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 8px;
  margin-top: 8px;

  &--icon {
    width: 16px;
    height: 16px;
  }

  &--message {
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: $error;
  }
}
</style>
